import { Button } from '@components/button';
import { DataTable } from '@components/dataTable';
import { StaffUser } from '@root/globalTypes';
import { ColumnDef } from '@tanstack/react-table';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { DisableStaffUserButton } from './disableUserButton';
import { EnableStaffUserButton } from './enableUserButton';
import { ResendStaffUserInvitationButton } from './resendInvitationButton';
import { UserStatusBadge } from './statusBadge';
import { UseMutateFunction } from '@tanstack/react-query';
import { useCheckUserPermissions } from '@hooks/checkUserPermisions';
import { useTableSorting } from '@hooks/useTableSorting';
import { TableDateCell } from '@components/table/dateCell';

type StaffUsersTableProps = {
  data: StaffUser[];
  visibleColumns?: string[];
  onRowSelect: (id: string) => void;
  onDisable: UseMutateFunction<StaffUser, Error, string, unknown>;
  onEnable: UseMutateFunction<StaffUser, Error, string, unknown>;
  onResend: UseMutateFunction<{ message: string }, Error, string, unknown>;
};

export function useStaffUsersTable({
  data,
  visibleColumns,
  onRowSelect,
  onDisable,
  onEnable,
  onResend,
}: StaffUsersTableProps) {
  const { t } = useTranslation('app');
  const { verifyUserPermission } = useCheckUserPermissions();
  const { sorting, updateSorting } = useTableSorting();

  const columns: ColumnDef<StaffUser>[] = [
    {
      id: 'name',
      accessorKey: 'name',
      header: t('table.name'),
      sortDescFirst: true,
      enableSorting: true,
    },
    {
      id: 'email',
      accessorKey: 'email',
      header: t('table.email'),
      sortDescFirst: true,
      enableSorting: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: t('table.status'),
      enableSorting: false,
      cell: ({ row }) => <UserStatusBadge active={row.original.active} />,
    },
    {
      id: 'created_at',
      accessorKey: 'createdAt',
      header: t('table.createdAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.createdAt} />,
    },
    {
      id: 'updated_at',
      accessorKey: 'updatedAt',
      header: t('table.updatedAt'),
      sortDescFirst: true,
      enableSorting: true,
      cell: ({ row }) => <TableDateCell date={row.original.updatedAt} />,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: t('table.actions'),
      enableSorting: false,
      cell: ({ row }) => {
        const rowUser = row.original;
        return (
          <div className="flex items-center gap-2">
            {verifyUserPermission('staff_users_read') && (
              <Button
                variant="primary"
                size="sm"
                className="w-14"
                onClick={() => onRowSelect(rowUser.id)}>
                <PencilIcon className="h-5" />
              </Button>
            )}
            <EnableStaffUserButton user={rowUser} onEnable={onEnable} />
            <DisableStaffUserButton user={rowUser} onDisable={onDisable} />
            <ResendStaffUserInvitationButton user={rowUser} onResend={onResend} />
          </div>
        );
      },
    },
  ];

  return {
    columns,
    Table: () => {
      return (
        <DataTable
          columns={
            visibleColumns
              ? columns.filter(({ id }) =>
                  typeof id === 'string' ? visibleColumns.includes(id) : false,
                )
              : columns
          }
          data={data}
          sorting={sorting}
          onSortChange={updateSorting}
        />
      );
    },
  };
}
